import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ProfileHeader } from './profile/ProfileHeader';
import { ProfileStats } from './profile/ProfileStats';
import { EditProfileForm } from './profile/EditProfileForm';
import { ChangeEmailForm } from './profile/ChangeEmailForm';
import { PrivacySettings } from './profile/PrivacySettings';
import { DataControls } from './profile/DataControls';
import { PrivateProfileNotice } from './profile/PrivateProfileNotice';
import { getToken, updateUserData } from '@/utils/auth';
import { logger } from '@/utils/logger';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UserProfile = ({ userId, isOpen, onClose, currentUserId, onAccountDeleted }) => {
  const [stats, setStats] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ bio: '', avatar: '' });

  const fetchUserStats = useCallback(async (silent = false) => {
    if (!userId) return;
    try {
      if (!silent) setLoading(true);
      setIsPrivate(false);
      const token = getToken();
      const response = await axios.get(`${API}/users/${userId}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      setEditData({
        bio: response.data.user.bio || '',
        avatar: response.data.user.avatar
      });
    } catch (error) {
      if (error.response?.status === 403) {
        setStats(null);
        setIsPrivate(true);
        return;
      }
      logger.error('Failed to fetch user stats:', error);
      toast.error('Failed to load profile');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserStats();
    }
  }, [isOpen, userId, fetchUserStats]);

  const handleUpdateProfile = async () => {
    try {
      const token = getToken();
      await axios.put(`${API}/users/me`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      updateUserData({ bio: editData.bio, avatar: editData.avatar });
      
      toast.success('Profile updated successfully');
      setIsEditing(false);
      fetchUserStats();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (stats) {
      setEditData({
        bio: stats.user.bio || '',
        avatar: stats.user.avatar
      });
    }
  };

  const isOwnProfile = userId === currentUserId;

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl border-[#E4E4E7]">
          <DialogTitle className="sr-only">Loading Profile</DialogTitle>
          <DialogDescription className="sr-only">Loading user profile information</DialogDescription>
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#0047FF] border-t-transparent rounded-full animate-spin"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (isPrivate) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md border-[#E4E4E7] p-0" data-testid="user-profile-modal">
          <DialogTitle className="sr-only">Private profile</DialogTitle>
          <DialogDescription className="sr-only">This player's profile is private</DialogDescription>
          <PrivateProfileNotice onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  if (!stats) return null;

  const { user, rank, total_players, favorite_game_name, games_played_count } = stats;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl border-[#E4E4E7] p-0 max-h-[90vh] overflow-y-auto [&>button.absolute]:hidden" data-testid="user-profile-modal">
        <DialogTitle className="sr-only">User Profile</DialogTitle>
        <DialogDescription className="sr-only">
          View and edit user profile information
        </DialogDescription>
        <ProfileHeader
          user={user}
          rank={rank}
          isOwnProfile={isOwnProfile}
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onClose={onClose}
        />

        <div className="p-6 space-y-6">
          {isEditing ? (
            <>
              <EditProfileForm
                editData={editData}
                setEditData={setEditData}
                onSave={handleUpdateProfile}
                onCancel={handleCancelEdit}
              />
              <div className="border-t border-[#E4E4E7] pt-6">
                <ChangeEmailForm
                  currentEmail={user.email}
                  onEmailChanged={() => fetchUserStats(true)}
                />
              </div>
            </>
          ) : (
            <div>
              <h3 className="text-sm font-medium text-[#71717A] uppercase tracking-wider mb-2">Bio</h3>
              <p className="text-[#09090B] text-sm">
                {user.bio || 'No bio yet. Click Edit Profile to add one!'}
              </p>
            </div>
          )}

          <ProfileStats
            user={user}
            rank={rank}
            totalPlayers={total_players}
            gamesPlayedCount={games_played_count}
            favoriteGameName={favorite_game_name}
          />

          {isOwnProfile && (
            <div className="border-t border-[#E4E4E7] pt-6 space-y-6" data-testid="profile-settings-section">
              <PrivacySettings
                visibility={user.profile_visibility}
                onChanged={(v) => setStats((prev) => ({ ...prev, user: { ...prev.user, profile_visibility: v } }))}
              />
              <DataControls username={user.username} onAccountDeleted={onAccountDeleted} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
